import dayjs from 'dayjs'

import { getDiffInDays } from '@/src/general/primitives/dates/dayjs'

import type { TGetSubscription, TGetSubscriptionPublication } from '../api/getSubscriptions.types'
import { COMMUNITY_LIFETIME, COMMUNITY_PUBLICATION_FREQUENCY } from '../models/community.constants'

type TComputedSubscriptionPublication = TGetSubscriptionPublication & { rank: number | null }

export type TComputedSubscription = TGetSubscription & {
  /** The progress of the subscription as a percentage (0-100) */
  progress: number | undefined
  /** The last publication the user received in the scope of the subscription */
  lastPublicationReceived: TComputedSubscriptionPublication | undefined
  /**
   * The next publication to pray:
   * - the last publication if the community is everlasting
   * - the last publication received if the journey is synced with an ongoing community
   * - the first unprayed publication otherwise
   */
  nextPublication: TComputedSubscriptionPublication | undefined
  isPrayedToday: boolean
}

export const getComputedSubscription = (subscription: TGetSubscription): TComputedSubscription => {
  if (
    subscription.community.lifetime === COMMUNITY_LIFETIME.EVERLASTING ||
    !subscription.community.toDate ||
    !subscription.community.fromDate ||
    !subscription.toDate
  ) {
    const lastPublicationReceived = subscription.community.publications[0]
      ? { ...subscription.community.publications[0], rank: null }
      : undefined
    const nextPublication = lastPublicationReceived
    const isPrayedToday = !!lastPublicationReceived?.prayers?.[0]

    return { lastPublicationReceived, nextPublication, isPrayedToday, progress: undefined, ...subscription }
  }

  const communityDuration = getDiffInDays(subscription.community.fromDate, subscription.community.toDate)
  const isSubscriptionSyncedWithCommunity = subscription.offset === 0
  const shouldUseLastPublicationReceived =
    isSubscriptionSyncedWithCommunity && !dayjs(subscription.community.toDate).isBefore(dayjs(), 'day')

  let publicationsCount: number

  if (dayjs(subscription.community.toDate).isBefore(dayjs())) {
    publicationsCount = subscription.community.publications.length
  } else {
    switch (subscription.community.publicationFrequencyType) {
      case COMMUNITY_PUBLICATION_FREQUENCY.DAILY:
        publicationsCount = communityDuration
        break
      case COMMUNITY_PUBLICATION_FREQUENCY.MONTHLY:
        publicationsCount = (communityDuration * (subscription.community.publicationFrequencyValue ?? 1)) / 30
        break
      default:
        publicationsCount = communityDuration / 7
        break
    }
  }

  let prayedPublicationsCount = 0
  let lastPublicationReceived: TComputedSubscription['lastPublicationReceived'] = undefined
  let nextPublication: TComputedSubscription['nextPublication'] = undefined
  let isPrayedToday = false

  for (let index = 0; index < subscription.community.publications.length; index++) {
    const publication = subscription.community.publications[index]
    const subscriptionPublicationDate = dayjs(publication.effectivePublicationDate).add(subscription.offset, 'days')
    const daysToPublication = getDiffInDays(dayjs(), subscriptionPublicationDate)

    if (daysToPublication <= 0) {
      lastPublicationReceived = { ...publication, rank: index + 1 }
    }

    if (
      isSubscriptionSyncedWithCommunity &&
      dayjs(publication.effectivePublicationDate).isBefore(subscription.fromDate)
    ) {
      // Skip publications published before the subscription.fromDate, when the subscription is synced with the community,
      // (i.e. the user joined the community mid-way) so that these missed publications are ignored in the following calculus
      continue
    }

    if (publication.prayers?.[0]) {
      prayedPublicationsCount++
      if (!isPrayedToday && publication.prayers.some((prayer) => dayjs(prayer.createdAt).isSame(dayjs(), 'day'))) {
        isPrayedToday = true
      }
    } else if (
      !shouldUseLastPublicationReceived &&
      (!nextPublication ||
        dayjs(publication.effectivePublicationDate).isBefore(nextPublication.effectivePublicationDate))
    ) {
      nextPublication = { ...publication, rank: index + 1 }
    }
  }

  if (shouldUseLastPublicationReceived) {
    nextPublication = lastPublicationReceived
  }

  const progress = publicationsCount > 0 ? (prayedPublicationsCount / publicationsCount) * 100 : 0

  return { ...subscription, progress, lastPublicationReceived, nextPublication, isPrayedToday }
}
